// Lightweight in-repo HTML sanitizer.
//
// Used by the Management Guidelines Patient Preview (PRD 07 §3.2) — and by
// any other surface that needs to render user-authored HTML — without pulling
// in an external dependency (per project rule: no new deps without approval).
//
// Strategy: parse the input into a detached <template>, walk the tree, drop
// any tag outside the allow-list, drop any attribute outside the per-tag
// allow-list, and reject hrefs whose scheme isn't http/https/mailto. This is
// stricter than the prototype (which just renders innerHTML raw) and protects
// the React render path from script injection or javascript: URLs.
//
// The DOM-based approach runs in the same browser the user will view the
// content in, so we don't have to maintain a separate HTML parser. SSR is not
// a concern for the admin app.

const GLOBAL_ATTRS = new Set(['style', 'class']);

// Allowed tag → per-tag attribute allow-list. The values are merged with
// GLOBAL_ATTRS so `style` is universally allowed (the WYSIWYG output relies
// on `style="color: ..."` for the color palette + `style="font-size: ..."`
// from execCommand fontSize).
const ALLOWED_TAGS: Record<string, ReadonlySet<string>> = {
  p: new Set(),
  br: new Set(),
  h1: new Set(),
  h2: new Set(),
  h3: new Set(),
  div: new Set(),
  span: new Set(),
  strong: new Set(),
  b: new Set(),
  em: new Set(),
  i: new Set(),
  u: new Set(),
  s: new Set(),
  strike: new Set(),
  blockquote: new Set(),
  ul: new Set(),
  ol: new Set(),
  li: new Set(),
  a: new Set(['href', 'target', 'rel']),
  // execCommand('fontSize') emits <font size="N"> nodes; execCommand('foreColor')
  // emits <font color="#xxx">. Allowed here so the round-trip preserves
  // formatting users applied via the toolbar.
  font: new Set(['size', 'color', 'face']),
};

const SAFE_URL_SCHEME = /^(https?:|mailto:)/i;
// Empty string covers fragment-only or relative hrefs which are also fine.
function isSafeHref(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return true;
  return SAFE_URL_SCHEME.test(trimmed);
}

function sanitizeElement(el: Element): void {
  const tag = el.tagName.toLowerCase();
  const allowedAttrs = ALLOWED_TAGS[tag];

  if (!allowedAttrs) {
    // Unknown tag — unwrap (keep children) so the user doesn't lose text. The
    // alternative (remove) is too aggressive when the input is mostly safe
    // but contains an odd <table> or similar.
    const parent = el.parentNode;
    if (parent) {
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
    return;
  }

  // Iterate over a snapshot — removing attributes mutates the live list.
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const allowed = allowedAttrs.has(name) || GLOBAL_ATTRS.has(name);
    if (!allowed) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (tag === 'a' && name === 'href' && !isSafeHref(attr.value)) {
      el.removeAttribute(attr.name);
      continue;
    }
    // Inline event handlers (onclick=…) never pass the allow-list check, but
    // double-check anyway since they're the worst-case payload.
    if (name.startsWith('on')) {
      el.removeAttribute(attr.name);
    }
  }

  // For <a>, force rel=noopener+noreferrer when target=_blank so we don't
  // accidentally hand window.opener to user-provided URLs.
  if (tag === 'a' && el.getAttribute('target') === '_blank') {
    el.setAttribute('rel', 'noopener noreferrer');
  }
}

function walk(root: Element): void {
  // Collect first, mutate after — mutation during traversal causes the
  // NodeIterator to skip siblings.
  const elements: Element[] = [];
  const it = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = it.nextNode();
  while (node) {
    elements.push(node as Element);
    node = it.nextNode();
  }
  for (const el of elements) sanitizeElement(el);
}

export function sanitizeHtml(input: string): string {
  if (!input) return '';
  const template = document.createElement('template');
  template.innerHTML = input;
  walk(template.content as unknown as Element);
  return template.innerHTML;
}
